import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import InputField from "components/common/Input/Input";
import AddIcon from "assets/medication/Add.svg";
import DropdownWithIconInBoundary from "./components/SelectDropdown";
import { ReactComponent as MyIcon } from "assets/medication/Vector.svg";
import { ReactComponent as TimeIcon } from "assets/medication/Time.svg";
import RemoveIcon from "assets/medication/Delete.svg";
import CustomButton from "components/common/Button/CustomButton";
import {
  CHOOSE_EVENT,
  MEDICINE_EVENT,
  MEDICINE_TYPE,
  USER_ROLE,
  UNIT_OF_MEASURE,
} from "Constants/constant";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import { addMonths } from "Utils/utils";
import { MdLibraryAdd } from "react-icons/md";
import { apiGetRequest, apiPostRequest, apiPutRequest, apiDeleteRequest } from "helpers/Requests";
import {
  URL_PARTICIPANT_MEDICATION_TABLE_LIST,
  URL_PARTICIPANT_MEDICATION_TABLE_LIST_HRM,
  URL_PARTICIPANT_STORE_MEDICATION,
  URL_PARTICIPANT_UPDATE_MEDICATION,
} from "store/services/URL";
import { toast } from "react-toastify";
import {
  roleAccess,
  canAccessParticipantMedicationAction,
  usePermissionsVersion,
} from "helpers/RolesPermission";
import { roleAuthenticateUserRequest } from "store/actions/auth.actions";
import { IoTrashOutline } from "react-icons/io5";
import { CiEdit } from "react-icons/ci";
import { FiSave } from "react-icons/fi";
import { FaList, FaPlus, FaTable, FaChevronDown, FaChevronRight, FaExclamationTriangle, FaPills, FaNotesMedical, FaCopy } from "react-icons/fa";
import { MdViewList, MdViewModule } from "react-icons/md";
import { HiExclamationCircle } from "react-icons/hi";
import Loader from "components/common/Loader/Loader";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import DeleteModal from "./components/DeleteModal";
import moment from "moment";
import { convertTo12HourFormat } from "Utils/utils";
import SelectOption from "components/common/Select/Select";
import PopoverContainer from "components/common/Popover/PopoverContainer";

const MEDICATION_PRACTITIONER_WARNING =
  "Medication listing must be added/ updated by an appropriate medical practitioner";

const MedicationPractitionerWarning = () => (
  <div
    role='alert'
    className='flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900'
  >
    <FaExclamationTriangle
      className='mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600'
      aria-hidden
    />
    <p>
      <span className='font-semibold'>Warning: </span>
      {MEDICATION_PRACTITIONER_WARNING}
    </p>
  </div>
);

const MedicationApplicationRecord = ({ participantInfo }) => {
  const dispatch = useDispatch();
  const [medicationLoader, setMedicationLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // "all", "routine", "prn"
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded rows
  const [cloneCounts, setCloneCounts] = useState({}); // Track clone count for each medication (by original medication ID)
  const cloneValidationToastShownAt = useRef(0); // Throttle "complete current medication" toast when clone is clicked repeatedly
  const addValidationToastShownAt = useRef(0); // Throttle "complete current medication" toast when add is clicked repeatedly

  const permissionsVersion = usePermissionsVersion();

  // Pull latest role toggles when opening the list (manager session may be stale).
  useEffect(() => {
    dispatch(roleAuthenticateUserRequest());
  }, [dispatch]);

  const canEditMedication =
    permissionsVersion >= 0 && canAccessParticipantMedicationAction("EDIT");
  const canCloneMedication =
    permissionsVersion >= 0 && canAccessParticipantMedicationAction("CLONE");
  const canDeleteMedication =
    permissionsVersion >= 0 && canAccessParticipantMedicationAction("DELETE");
  const canShowMedicationActions =
    canEditMedication || canCloneMedication || canDeleteMedication;

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      medicine_name: "",
      allergic_name: "",
      reaction_type: "",
      allergic_note: "",
      medications: [
        {
          medicine_type: "",
          medicine_name: "",
          event_type: "",
          time: "",
          select_quantity: "",
          event_name: "",
          duration_start: null,
          toDate: null,
          isApiData: false,
          unit_of_measure: "",
          // quantity: "",
          instructions: "",
          side_effects: "",
          // ADRS fields for this medication
          adrs_medicine_name: "",
          adrs_allergic_name: "",
          adrs_reaction_type: "",
          adrs_allergic_note: "",
        },
      ],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "medications",
  });
  const [loaders, setLoaders] = useState(false);
  const [editableIndex, setEditableIndex] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectDeletedMedication, setSelectedDeletedMedication] =
    useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const watchAllFields = watch();

  // Filter options for medication type dropdown
  const filterTypeOptions = [
    { label: "All Types", value: "all" },
    { label: "Routine", value: "routine" },
    { label: "PRN", value: "prn" },
  ];

  // Handler for filter type change
  const handleFilterTypeChange = (selectedOption) => {
    if (selectedOption) {
      setFilterType(selectedOption.value);
    }
  };

  // Get selected option object for SelectOption component
  const selectedFilterOption = filterTypeOptions.find(
    (option) => option.value === filterType
  ) || filterTypeOptions[0];

  // Helper function to set field-level errors for a medication
  const setMedicationFieldErrors = (index, medication) => {
    const {
      medicine_type,
      medicine_name,
      event_type,
      time,
      select_quantity,
      event_name,
      duration_start,
      unit_of_measure,
    } = medication;

    // Clear all errors first
    clearErrors(`medications[${index}].medicine_type`);
    clearErrors(`medications[${index}].medicine_name`);
    clearErrors(`medications[${index}].event_type`);
    clearErrors(`medications[${index}].event_name`);
    clearErrors(`medications[${index}].time`);
    clearErrors(`medications[${index}].duration_start`);
    clearErrors(`medications[${index}].select_quantity`);
    clearErrors(`medications[${index}].unit_of_measure`);

    let hasErrors = false;

    if (!medicine_type) {
      setError(`medications[${index}].medicine_type`, {
        type: "required",
        message: "Medicine Type is required",
      });
      hasErrors = true;
    }

    if (!medicine_name) {
      setError(`medications[${index}].medicine_name`, {
        type: "required",
        message: "Medicine Name is required",
      });
      hasErrors = true;
    }

    if (!event_type) {
      setError(`medications[${index}].event_type`, {
        type: "required",
        message: "Event is required",
      });
      hasErrors = true;
    } else {
      // For Routine medications, event_type cannot be 'prn'
      if (medicine_type === "routine" && event_type === "prn") {
        setError(`medications[${index}].event_type`, {
          type: "validate",
          message: "Routine medications cannot have PRN as event type",
        });
        hasErrors = true;
      } else {
        // Event-specific validations
        if (event_type === "event" && !event_name) {
          setError(`medications[${index}].event_name`, {
            type: "required",
            message: "Choose Event is required",
          });
          hasErrors = true;
        } else if (event_type === "time" && !time) {
          setError(`medications[${index}].time`, {
            type: "required",
            message: "Time is required",
          });
          hasErrors = true;
        }
      }
    }

    if (!duration_start) {
      setError(`medications[${index}].duration_start`, {
        type: "required",
        message: "Start Date is required",
      });
      hasErrors = true;
    }

    // Quantity is only required if event_type is NOT 'prn'
    const isQuantityRequired = event_type !== "prn";
    if (isQuantityRequired) {
      if (select_quantity === undefined || select_quantity === null || select_quantity === "") {
        setError(`medications[${index}].select_quantity`, {
          type: "required",
          message: "Quantity is required",
        });
        hasErrors = true;
      } else {
        const numValue = parseFloat(select_quantity);
        if (isNaN(numValue)) {
          setError(`medications[${index}].select_quantity`, {
            type: "validate",
            message: "Quantity must be a valid number",
          });
          hasErrors = true;
        } else if (numValue < 0) {
          setError(`medications[${index}].select_quantity`, {
            type: "validate",
            message: "Quantity must be 0 or greater",
          });
          hasErrors = true;
        }
      }
    }

    if (!unit_of_measure || unit_of_measure === "") {
      setError(`medications[${index}].unit_of_measure`, {
        type: "required",
        message: "Unit of Measure is required",
      });
      hasErrors = true;
    }

    return hasErrors;
  };

  // Helper function to validate a single medication and return error message (for backward compatibility)
  const validateMedicationFields = (medication) => {
    const {
      medicine_type,
      medicine_name,
      event_type,
      time,
      select_quantity,
      event_name,
      duration_start,
      unit_of_measure,
    } = medication;

    const missingFields = [];

    if (!medicine_type) {
      missingFields.push("Medicine Type");
    }

    if (!medicine_name) {
      missingFields.push("Medicine Name");
    }

    if (!event_type) {
      missingFields.push("Event");
    } else {
      // For Routine medications, event_type cannot be 'prn'
      if (medicine_type === "routine" && event_type === "prn") {
        missingFields.push("Event (Routine medications cannot have PRN as event type)");
      } else {
        // Event-specific validations
        if (event_type === "event" && !event_name) {
          missingFields.push("Choose Event");
        } else if (event_type === "time" && !time) {
          missingFields.push("Time");
        }
      }
    }

    if (!duration_start) {
      missingFields.push("Start Date");
    }

    // Quantity is only required if event_type is NOT 'prn'
    const isQuantityRequired = event_type !== "prn";
    if (isQuantityRequired) {
      if (select_quantity === undefined || select_quantity === null || select_quantity === "") {
        missingFields.push("Quantity");
      } else {
        const numValue = parseFloat(select_quantity);
        if (isNaN(numValue) || numValue < 0) {
          missingFields.push("Quantity (must be 0 or greater)");
        }
      }
    }

    if (!unit_of_measure || unit_of_measure === "") {
      missingFields.push("Unit of Measure");
    }

    if (missingFields.length > 0) {
      return {
        isValid: false,
        message: `Please fill in the following required fields: ${missingFields.join(", ")}`,
        missingFields,
      };
    }

    return { isValid: true, message: "", missingFields: [] };
  };

  // Helper function to validate a single medication (for boolean checks)
  const isMedicationSafe = (medication) => {
    const validation = validateMedicationFields(medication);
    return validation.isValid;
  };

  const validateFields = () => {
    const medications = watchAllFields.medications;

    for (let i = 0; i < medications.length; i++) {
      if (!isMedicationSafe(medications[i])) {
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    getMedicationList();
  }, [participantInfo?.uuid, replace]);

  // Phase 2: Sync form values when entering edit mode
  // This ensures instructions, side_effects, and unit_of_measure are populated
  useEffect(() => {
    if (editableIndex !== null && editableIndex >= 0 && editableIndex < fields.length) {
      const field = fields[editableIndex];
      const apiData = field?.item; // Original API response item
      const currentFormData = watch(`medications[${editableIndex}]`);
      
      // Priority: API data (field.item) > Form data (currentFormData)
      // Get values from API data first, then fallback to form data
      // Note: API returns side_effect (singular) and unit_of_measurement, but we use side_effects and unit_of_measure in form
      // Check multiple possible field names for instructions
      const instructionsValue = apiData?.medication_instruction ?? apiData?.medication_instructions ?? apiData?.instruction ?? apiData?.instructions ?? currentFormData?.instructions ?? "";
      const sideEffectsValue = apiData?.side_effect ?? apiData?.side_effects ?? currentFormData?.side_effects ?? "";
      const unitOfMeasureValue = apiData?.unit_of_measurement ?? apiData?.unit_of_measure ?? currentFormData?.unit_of_measure ?? "";
      
      // Only set if the value is different from current form value
      if (instructionsValue !== currentFormData?.instructions) {
        setValue(`medications[${editableIndex}].instructions`, instructionsValue, { shouldValidate: false });
      }
      
      if (sideEffectsValue !== currentFormData?.side_effects) {
        setValue(`medications[${editableIndex}].side_effects`, sideEffectsValue, { shouldValidate: false });
      }
      
      if (unitOfMeasureValue !== currentFormData?.unit_of_measure) {
        setValue(`medications[${editableIndex}].unit_of_measure`, unitOfMeasureValue, { shouldValidate: false });
      }
    }
  }, [editableIndex, fields, watch, setValue]);

  const getMedicationList = async () => {
    setMedicationLoader(true);
    const URL = roleAccess([USER_ROLE?.Sp_Standard_User])
      ? URL_PARTICIPANT_MEDICATION_TABLE_LIST_HRM
      : URL_PARTICIPANT_MEDICATION_TABLE_LIST;
    apiGetRequest(`${URL}?pt_id=${participantInfo?.uuid}`)
      .then((res) => {
        setMedicationLoader(false);
        if (res.status === 200 && Array.isArray(res?.data?.data)) {
          const formatted = res.data.data
            .filter((item) => item.id !== null) // Filter out dummy medication records (ADRS-only entries)
            .map((item) => ({
              item: item,
              isApiData: true,
              medicine_type: item.medicine_type || "",
              medicine_name: item.medicine_name || "",
              event_type: item.event_type || "",
              time: item.event_type === "time" ? item.time : "",
              event_name: item.event_type === "event" ? item.event_name : "",
              select_quantity: item.select_quantity || "",
              duration_start: item.duration_start || null,
              duration_end: item.duration_end || null,
              // API returns unit_of_measurement, map it to unit_of_measure for form consistency
              unit_of_measure: item.unit_of_measurement || item.unit_of_measure || "",
              // quantity: item.quantity || "",
              // Check multiple possible field names for instructions (API might use different variations)
              instructions: item.medication_instruction || item.medication_instructions || item.instruction || item.instructions || "",
              // API returns side_effect (singular), map it to side_effects (plural) for form consistency
              side_effects: item.side_effect || item.side_effects || "",
              // ADRS fields linked to this medication
              adrs_medicine_name: item.allergic_medicine_name || "",
              adrs_allergic_name: item.allergic_name || "",
              adrs_reaction_type: item.reaction_type || "",
              adrs_allergic_note: item.allergic_note || "",
              adrs_uuid: item.adrs_uuid || null,
            }));
          
          // Always replace with formatted data (empty array if no medications)
          // This ensures default empty medication is cleared
          replace(formatted);
          
          // Ensure form values are synced after replace
          // This is needed because replace() might not immediately update all form values
          setTimeout(() => {
            formatted.forEach((medication, index) => {
              // Get instructions value from the formatted medication (which already has the mapped value)
              if (medication.instructions !== undefined && medication.instructions !== null && medication.instructions !== "") {
                setValue(`medications[${index}].instructions`, medication.instructions, { shouldValidate: false });
              }
              if (medication.side_effects !== undefined && medication.side_effects !== null && medication.side_effects !== "") {
                setValue(`medications[${index}].side_effects`, medication.side_effects, { shouldValidate: false });
              }
              if (medication.unit_of_measure !== undefined && medication.unit_of_measure !== null && medication.unit_of_measure !== "") {
                setValue(`medications[${index}].unit_of_measure`, medication.unit_of_measure, { shouldValidate: false });
              }
            });
          }, 100);
        } else {
          // If response is not in expected format, clear default medication
          replace([]);
        }
      })
      .catch((err) => {
        setMedicationLoader(false);
        // Clear default medication even on error to prevent incorrect count
        replace([]);
        toast.error(err.message || "Something went wrong");
      });
  };
  const onSubmit = (data) => {
    const medicationsToSave = data?.medications?.filter((medication) => !medication.isApiData);
    
    if (!medicationsToSave || medicationsToSave.length === 0) {
      toast.error("No medications to save.");
      return;
    }

    // Validate each medication and set field-level errors
    let hasAnyErrors = false;
    medicationsToSave.forEach((medication, originalIndex) => {
      // Find the actual index in the full medications array
      const actualIndex = data.medications.findIndex((med) => med === medication);
      if (actualIndex !== -1) {
        const hasErrors = setMedicationFieldErrors(actualIndex, medication);
        if (hasErrors) {
          hasAnyErrors = true;
        }
      }
    });

    if (hasAnyErrors) {
      return;
    }

    setLoaders(true);

    const medications = medicationsToSave.map((medication) => {
        const payloadMedication = { ...medication };

        if (medication.event_type === "event") {
          Object.assign(payloadMedication, {
            event_name: medication.event_name,
          });
          delete payloadMedication.time;
        } else if (medication.event_type === "time") {
          Object.assign(payloadMedication, { event_name: medication.time });
          delete payloadMedication.event_name;
        }

        // Include ADRS data for this medication
        // Set allergic_and_drug_reaction to true if any ADRS field has data for this medication
        const hasAdrsData = !!(
          (medication.adrs_medicine_name && medication.adrs_medicine_name.trim()) ||
          (medication.adrs_allergic_name && medication.adrs_allergic_name.trim()) ||
          (medication.adrs_reaction_type && medication.adrs_reaction_type.trim()) ||
          (medication.adrs_allergic_note && medication.adrs_allergic_note.trim())
        );

        // Add ADRS fields to payload (will be used to create medication record)
        payloadMedication.allergic_and_drug_reaction = hasAdrsData;
        payloadMedication.adrs_medicine_name = medication.adrs_medicine_name?.trim() || "";
        payloadMedication.adrs_allergic_name = medication.adrs_allergic_name?.trim() || "";
        payloadMedication.adrs_reaction_type = medication.adrs_reaction_type?.trim() || "";
        payloadMedication.adrs_allergic_note = medication.adrs_allergic_note?.trim() || "";

        return payloadMedication;
      });

    const payload = {
      pt_id: participantInfo.id,
      medications: medications,
    };

    apiPostRequest(URL_PARTICIPANT_STORE_MEDICATION, payload)
      .then((res) => {
        setLoaders(false);
        if (res.status === 201) {
          toast.success("Medication Record Added Successfully");
          getMedicationList();
          reset({
            medicine_name: "",
            allergic_name: "",
            reaction_type: "",
            allergic_note: "",
          });
          // const clearFieldsButton =
          //   document.getElementById("clearFieldsButton");
          // if (clearFieldsButton) {
          //   clearFieldsButton.click();
          // }
        }
      })
      .catch((err) => {
        setLoaders(false);
        toast.error(err.message || "Something went wrong");
      });
  };

  const handleResetAll = () => {
    const filteredFields = watchAllFields.medications.filter(
      (medication) => medication.isApiData
    ).map((medication) => ({
      ...medication,
      // Reset ADRS fields
      adrs_medicine_name: "",
      adrs_allergic_name: "",
      adrs_reaction_type: "",
      adrs_allergic_note: "",
    }));

    reset({
      medications: [...filteredFields],
    });
  };

  const handleDeleteMedication = async (index, medication) => {
    setSelectedDeletedMedication(medication);
    setSelectedIndex(index);
    setOpenDeleteModal(true);
  };
  const [editLoader, setEditLoader] = useState(false);
  const handleEdit = (index, item) => {
    const currentRow = watch(`medications[${index}]`);
    
    // Validate medication and set field-level errors
    const hasErrors = setMedicationFieldErrors(index, currentRow);
    if (hasErrors) {
      return;
    }

    setEditLoader(true);
    const {
      medicine_type,
      medicine_name,
      select_quantity,
      event_type,
      duration_start,
      duration_end,
      unit_of_measure,
      // quantity,
      instructions,
      side_effects,
      adrs_medicine_name,
      adrs_allergic_name,
      adrs_reaction_type,
      adrs_allergic_note,
    } = currentRow;

    // Set allergic_and_drug_reaction to true if any ADRS field has data
    const hasAdrsData = !!(
      (adrs_medicine_name && adrs_medicine_name.trim()) ||
      (adrs_allergic_name && adrs_allergic_name.trim()) ||
      (adrs_reaction_type && adrs_reaction_type.trim()) ||
      (adrs_allergic_note && adrs_allergic_note.trim())
    );

    // Check if this is a new medication (no ID) or existing medication (has ID)
    const medicationId = item?.item?.id;
    const isNewMedication = !medicationId;

    // Phase 3: Enhanced validation - Check start date for existing medications
    if (!isNewMedication && !duration_start) {
      toast.error("Start Date is required and cannot be removed for existing medications.");
      setEditLoader(false);
      return; // Prevent API call
    }

    if (isNewMedication) {
      // Use store endpoint for new medications
      const medicationPayload = {
        medicine_type,
        medicine_name,
        select_quantity,
        event_type,
        duration_start,
        duration_end,
        unit_of_measure,
        // quantity,
        instructions,
        side_effects,
        // Include ADRS data
        allergic_and_drug_reaction: hasAdrsData,
        adrs_medicine_name: adrs_medicine_name?.trim() || "",
        adrs_allergic_name: adrs_allergic_name?.trim() || "",
        adrs_reaction_type: adrs_reaction_type?.trim() || "",
        adrs_allergic_note: adrs_allergic_note?.trim() || "",
      };

      if (event_type === "event") {
        medicationPayload.event_name = currentRow.event_name;
      } else if (event_type === "time") {
        medicationPayload.time = currentRow.time;
      }

      const payload = {
        pt_id: participantInfo.id,
        medications: [medicationPayload],
      };

      apiPostRequest(URL_PARTICIPANT_STORE_MEDICATION, payload)
        .then((res) => {
          setEditLoader(false);
          if (res.status === 201) {
            toast.success("Medication Record Created Successfully");
            // Refresh the medication list
            getMedicationList();
          }
        })
        .catch((err) => {
          setEditLoader(false);
          toast.error(err.message || "Something went wrong");
        })
        .finally(() => {
          setEditableIndex(null);
          setEditLoader(false);
        });
    } else {
      // Use update endpoint for existing medications
      const payload = {
        medication_id: medicationId,
        medicine_type,
        medicine_name,
        select_quantity,
        event_type,
        duration_start,
        duration_end,
        unit_of_measure,
        // quantity,
        instructions,
        side_effects,
        // Include ADRS data
        allergic_and_drug_reaction: hasAdrsData,
        adrs_medicine_name: adrs_medicine_name?.trim() || "",
        adrs_allergic_name: adrs_allergic_name?.trim() || "",
        adrs_reaction_type: adrs_reaction_type?.trim() || "",
        adrs_allergic_note: adrs_allergic_note?.trim() || "",
      };

      if (event_type === "event") {
        payload.event_name = currentRow.event_name;
      } else if (event_type === "time") {
        payload.time = currentRow.time;
      }

      apiPostRequest(URL_PARTICIPANT_UPDATE_MEDICATION, payload)
        .then((res) => {
          setLoaders(false);
          if (res.status === 200) {
            toast.success("Medication Record Updated Successfully");
            // Refresh the medication list
            getMedicationList();
          }
        })
        .catch((err) => {
          setLoaders(false);
          toast.error(err.message || "Something went wrong");
        })
        .finally(() => {
          setEditableIndex(null);
          setEditLoader(false);
        });
    }
  };

  const closeModal = () => {
    setOpenDeleteModal(false);
  };

  const handleCloneMedication = (index, medication) => {
    // Check if there's already a newly added medication that is not safe
    const medications = watchAllFields.medications;
    const newlyAddedMedications = medications.filter(
      (med) => !med.isApiData
    );

    if (newlyAddedMedications.length > 0) {
      // Check if any newly added medication is not safe
      const unsafeMedication = newlyAddedMedications.find(
        (med) => !isMedicationSafe(med)
      );

      if (unsafeMedication) {
        const now = Date.now();
        if (now - cloneValidationToastShownAt.current < 3000) {
          return; // Avoid multiple toasts when user clicks clone repeatedly
        }
        cloneValidationToastShownAt.current = now;
        toast.error(
          "Please complete and save the current medication before cloning a new one."
        );
        return;
      }
    }

    // Get the current medication data
    const currentMedication = watch(`medications[${index}]`);
    
    // Determine the original medication ID (for tracking clones)
    const originalMedicationId = medication?.id || medication?.uuid || `temp-${index}`;
    
    // Increment clone count for this original medication
    const currentCount = cloneCounts[originalMedicationId] || 0;
    const newCount = currentCount + 1;
    setCloneCounts(prev => ({
      ...prev,
      [originalMedicationId]: newCount
    }));
    
    // Create a clone of the medication without the ID (so it's treated as new)
    const clonedMedication = {
      medicine_type: currentMedication.medicine_type || "",
      medicine_name: currentMedication.medicine_name || "",
      event_type: currentMedication.event_type || "",
      time: currentMedication.time || "",
      select_quantity: currentMedication.select_quantity || "",
      event_name: currentMedication.event_name || "",
      duration_start: null, // Reset start date for new medication
      duration_end: null, // Reset end date for new medication
      unit_of_measure: currentMedication.unit_of_measure || "",
      instructions: currentMedication.instructions || "",
      side_effects: currentMedication.side_effects || "",
      // ADRS fields
      adrs_medicine_name: currentMedication.adrs_medicine_name || "",
      adrs_allergic_name: currentMedication.adrs_allergic_name || "",
      adrs_reaction_type: currentMedication.adrs_reaction_type || "",
      adrs_allergic_note: currentMedication.adrs_allergic_note || "",
      isApiData: false, // Mark as newly added medication
      clonedFrom: originalMedicationId, // Track which medication this was cloned from
      cloneNumber: newCount, // Track clone number
    };

    // Calculate the new index before append
    const newIndex = fields.length;
    const newMedicationId = `temp-${newIndex}`;
    
    // Append the cloned medication
    append(clonedMedication);
    
    // Collapse all other rows and expand the new one
    setExpandedRows(new Set([newMedicationId]));
    setEditableIndex(newIndex);
    
    // Auto-scroll to the new row after DOM updates
    setTimeout(() => {
      const rowElement = document.querySelector(`[data-medication-id="${newMedicationId}"]`);
      if (rowElement) {
        rowElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 200);
  };

  const isMedicationManuallyAdded = fields.some(
    (medication) => !medication.isApiData
  );

  const UOM_LABELS = {
    tablet: "Tablet",
    capsule: "Capsule",
    half_tablet: "Half tablet",
    quarter_tablet: "Quarter tablet",
    milliliter: "mL",
    teaspoon: "tsp",
    tablespoon: "tbsp.",
    drops: "Drop(s)",
    vial: "Vial",
    bottle: "Bottle",
    units: "Unit(s)",
    patch: "Patch",
    puff: "Puff",
    sachet: "Sachet",
  };

  const shouldPluralizeDosageUnit = (quantity) => {
    const qty = parseFloat(quantity);
    return Number.isFinite(qty) && qty > 1;
  };

  const pluralizeUnitLabel = (label) => {
    if (!label || label.includes("(") || /[./]$/.test(label) || label.endsWith("s")) {
      return label;
    }
    const words = label.split(" ");
    const last = words[words.length - 1];
    const pluralLast =
      last.endsWith("ch") || last.endsWith("sh") || last.endsWith("x")
        ? `${last}es`
        : `${last}s`;
    words[words.length - 1] = pluralLast;
    return words.join(" ");
  };

  /** @param {string} uom @param {string|number} [quantity] — pluralize when quantity > 1 */
  const formatUOM = (uom, quantity) => {
    const singular = UOM_LABELS[uom] || uom;
    if (
      quantity !== undefined &&
      quantity !== null &&
      quantity !== "" &&
      shouldPluralizeDosageUnit(quantity)
    ) {
      return pluralizeUnitLabel(singular);
    }
    return singular;
  };

  const formatDosageDisplay = (quantity, uom) => {
    if (quantity === undefined || quantity === null || quantity === "") {
      return "N/A";
    }
    const unitLabel = formatUOM(uom, quantity);
    return unitLabel ? `${quantity} ${unitLabel}` : `${quantity}`;
  };

  // Helper function to format event name display (convert value to label)
  const formatEventName = (eventValue) => {
    if (!eventValue) return "N/A";
    const eventOption = CHOOSE_EVENT.find((event) => event.value === eventValue);
    return eventOption ? eventOption.label : eventValue;
  };

  // Helper function to check if medication is the default empty one
  // This helps prevent counting default empty medications during initial load
  const isDefaultEmptyMedication = (field, medData) => {
    return (
      !field.isApiData &&
      !field.item?.id &&
      !field.item?.uuid &&
      !medData?.medicine_name &&
      !medData?.medicine_type &&
      !medData?.event_type &&
      !medData?.quantity &&
      !medData?.unit_of_measure
    );
  };

  // Get medications from API data
  const apiMedications = fields.filter((field) => field.isApiData);

  // Filter medications based on search and filter (include both API and newly added medications)
  // Exclude default empty medications during initial load to prevent incorrect count display
  const filteredMedications = fields.filter((field, index) => {
    const medication = field.item;
    const medData = watchAllFields.medications[index];

    // Exclude default empty medications during initial load (V360-4706 fix)
    // This prevents showing "1 of 0" when no medications exist
    if (medicationLoader && isDefaultEmptyMedication(field, medData)) {
      return false;
    }

    // Search filter
    const matchesSearch =
      !searchTerm ||
      (medication?.medicine_name || medData?.medicine_name || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    // Type filter
    const matchesType =
      filterType === "all" ||
      (medication?.medicine_type || medData?.medicine_type) === filterType;

    return matchesSearch && matchesType;
  });

  // Toggle row expansion - only allow one row expanded at a time
  const toggleRow = (medicationId) => {
    const newExpanded = new Set();
    if (!expandedRows.has(medicationId)) {
      // If clicking on a collapsed row, expand it (and collapse others)
      newExpanded.add(medicationId);
    }
    // If clicking on an already expanded row, it will be empty (collapsed)
    setExpandedRows(newExpanded);
  };

  // Render Listing View
  const renderListView = () => {
    if (medicationLoader) {
      return (
        <div className='flex justify-center items-center w-full h-64'>
          <CircularLoader />
        </div>
      );
    }

    if (fields.length === 0) {
      return (
        <div className='bg-white rounded-lg p-8 text-center border border-gray-200'>
          <div className='text-gray-400 text-lg mb-2'>No medications found</div>
          <div className='text-gray-500 text-sm'>
            Add a new medication record using the "Add Medication" button above
          </div>
        </div>
      );
    }

    return (
      <>
        {filteredMedications.length === 0 ? (
          <div className='bg-white rounded-lg p-8 text-center border border-gray-200'>
            <div className='text-gray-400 text-lg mb-2'>
              No medications match your search
            </div>
            <div className='text-gray-500 text-sm'>
              Try adjusting your search or filter criteria
            </div>
          </div>
        ) : (
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
            <table className='w-full'>
              <thead className='bg-gradient-to-r from-gray-50 to-gray-100/50 border-b border-gray-200'>
                <tr>
                  <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-8'>
                    {/* Expand/Collapse column */}
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider max-w-[200px] w-[200px]'>
                    Medication Name
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                    Type
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                    Dosage
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                    Schedule
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                    Start Date
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                    End Date
                  </th>
                  {canShowMedicationActions && (
                    <th className='px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider'>
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className='bg-white divide-y divide-gray-200'>
                {filteredMedications.map((field, index) => {
                  const medication = field.item;
                  // Find the correct fieldIndex in the original fields array
                  const fieldIndex = fields.findIndex((f) => f === field);
                  const medData = watchAllFields.medications[fieldIndex];
                  const medicationId = medication?.id || `temp-${fieldIndex}`;
                  const isExpanded = expandedRows.has(medicationId);
                  const isEditing = editableIndex === fieldIndex;
                  const medicationName = medication?.medicine_name || medData?.medicine_name || "N/A";
                  const medicineType = medication?.medicine_type || medData?.medicine_type || "";
                  const dosageQuantity =
                    medication?.select_quantity ?? medData?.select_quantity;
                  const dosageUom =
                    medication?.unit_of_measure || medData?.unit_of_measure;
                  const dosage = formatDosageDisplay(dosageQuantity, dosageUom);
                  // Get event_type from either medication (API data) or medData (form data)
                  const eventType = medication?.event_type || medData?.event_type;
                  
                  // Get event_name value from any available source
                  const eventNameValue = medication?.event_name || medData?.event_name || medication?.item?.event_name;
                  
                  const schedule = eventType === "time"
                    ? convertTo12HourFormat(medication?.time || medData?.time) || "N/A"
                    : eventType === "event"
                    ? formatEventName(eventNameValue)
                    : eventType === "prn"
                    ? "PRN"
                    : "N/A";
                  
                  // Check if ADRS data exists (check both API field names and mapped field names)
                  const hasAdrsData = !!(medData?.adrs_medicine_name || medication?.allergic_medicine_name ||
                                         medData?.adrs_allergic_name || medication?.allergic_name ||
                                         medData?.adrs_reaction_type || medication?.reaction_type ||
                                         medData?.adrs_allergic_note || medication?.allergic_note);

                  return (
                    <React.Fragment key={medicationId}>
                      {/* Main Row */}
                      <tr
                        data-medication-id={medicationId}
                        className={`border-b border-gray-100 transition-all duration-200 ${
                          isExpanded 
                            ? "bg-gradient-to-r from-blue-50/50 to-transparent shadow-sm" 
                            : "hover:bg-gray-50/50 hover:shadow-sm"
                        }`}
                      >
                        <td className='px-4 py-3 whitespace-nowrap'>
                          <button
                            type='button'
                            onClick={() => {
                              // Only allow one row expanded at a time
                              if (isExpanded) {
                                setExpandedRows(new Set());
                              } else {
                                setExpandedRows(new Set([medicationId]));
                              }
                            }}
                            className='flex items-center justify-center w-7 h-7 hover:bg-blue-100 rounded-lg transition-all duration-200 group'
                            title={isExpanded ? 'Collapse details' : 'Expand details'}
                          >
                            {isExpanded ? (
                              <FaChevronDown className='w-3.5 h-3.5 text-blue-600 group-hover:text-blue-700' />
                            ) : (
                              <FaChevronRight className='w-3.5 h-3.5 text-gray-400 group-hover:text-blue-600' />
                            )}
                          </button>
                        </td>
                        <td className='px-4 py-3 max-w-[200px] w-[200px] min-w-0'>
                          <div className='flex items-center gap-2.5 min-w-0'>
                            <div className='text-sm font-semibold text-gray-900 truncate min-w-0' title={medicationName}>
                              {medicationName}
                            </div>
                            {hasAdrsData && (
                              <span
                                className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white shadow-sm hover:shadow-md transition-shadow'
                                title='Allergies & Adverse Drug Reactions (ADRS) information available'
                              >
                                <FaExclamationTriangle className='w-2.5 h-2.5' />
                              </span>
                            )}
                          </div>
                        </td>
                        <td className='px-4 py-3 whitespace-nowrap'>
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-lg shadow-sm ${
                              medicineType === "routine"
                                ? "bg-blue-50 text-blue-700 border border-blue-200"
                                : "bg-purple-50 text-purple-700 border border-purple-200"
                            }`}
                          >
                            {medicineType === "routine" ? "Routine" : "PRN"}
                          </span>
                        </td>
                        <td className='px-4 py-3 whitespace-nowrap'>
                          <div className='flex flex-col gap-0.5'>
                            <div className='flex items-center gap-2'>
                              <div className='text-sm font-semibold text-gray-900'>
                                {dosage}
                              </div>
                              {/* Show clone indicator if this medication was cloned */}
                              {/* {medData?.clonedFrom && medData?.cloneNumber && (
                                <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200'>
                                  Clone {medData.cloneNumber}
                                </span>
                              )} */}
                              {/* Show clone count on original medication if it has been cloned */}
                              {/* {!medData?.clonedFrom && medicationId && cloneCounts[medicationId] > 0 && (
                                <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200'>
                                  {cloneCounts[medicationId]} {cloneCounts[medicationId] === 1 ? 'Clone' : 'Clones'}
                                </span>
                              )} */}
                            </div>
                            {(medication?.unit_of_measure || medData?.unit_of_measure) && (
                              <div className='text-xs text-gray-500 font-medium'>
                                {formatUOM(dosageUom, dosageQuantity)}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className='px-4 py-3 whitespace-nowrap'>
                          <div className='flex flex-col gap-0.5'>
                            <div className='text-sm font-semibold text-gray-900'>{schedule}</div>
                            <div className='text-xs text-gray-500 font-medium'>
                              {medication?.event_type === "time"
                                ? "Time-based"
                                : medication?.event_type === "event"
                                ? "Event-based"
                                : "As Needed"}
                            </div>
                          </div>
                        </td>
                        <td className='px-4 py-3 whitespace-nowrap'>
                          <div className='text-sm font-medium text-gray-900'>
                            {medication?.duration_start
                              ? moment(medication.duration_start).format("DD/MM/YYYY")
                              : medData?.duration_start
                              ? moment(medData.duration_start).format("DD/MM/YYYY")
                              : <span className='text-gray-400'>N/A</span>}
                          </div>
                        </td>
                        <td className='px-4 py-3 whitespace-nowrap'>
                          <div className='text-sm font-medium text-gray-900'>
                            {medication?.duration_end
                              ? moment(medication.duration_end).format("DD/MM/YYYY")
                              : medData?.duration_end
                              ? moment(medData.duration_end).format("DD/MM/YYYY")
                              : <span className='text-gray-400'>-</span>}
                          </div>
                        </td>
                        {canShowMedicationActions && (
                          <td className='px-4 py-3 whitespace-nowrap'>
                            <div className='flex items-center gap-1.5' onClick={(e) => e.stopPropagation()}>
                              {canEditMedication && (
                              <button
                                type='button'
                                onClick={() => {
                                  const fieldIndex = fields.findIndex(
                                    (f) => f.item?.id === medication?.id
                                  );
                                  if (fieldIndex !== -1) {
                                    if (editableIndex === fieldIndex) {
                                      // Cancel button clicked - check if it's a new medication
                                      const isNewMedication = !field?.item?.id && !field?.item?.uuid && !medication?.id && !medication?.uuid;
                                      if (isNewMedication) {
                                        // Remove the new medication row
                                        remove(fieldIndex);
                                        // Also collapse the row if it was expanded
                                        const newExpanded = new Set(expandedRows);
                                        newExpanded.delete(medicationId);
                                        setExpandedRows(newExpanded);
                                      } else {
                                        // For saved medications, just exit edit mode
                                        setEditableIndex(null);
                                      }
                                    } else {
                                      // Phase 1: Explicitly populate form fields when entering edit mode
                                      // Get data from field.item (original API data) first, then fallback to medData (form data)
                                      // The API data is stored in field.item, and the formatted data is in the form values
                                      const apiData = field?.item; // Original API response item
                                      const formData = medData; // Current form data
                                      
                                      // Priority: API data (field.item) > Form data (medData)
                                      // Note: API returns side_effect (singular) and unit_of_measurement, but we use side_effects and unit_of_measure in form
                                      // Check multiple possible field names for instructions
                                      const instructionsValue = apiData?.medication_instruction ?? apiData?.medication_instructions ?? apiData?.instruction ?? apiData?.instructions ?? formData?.instructions ?? "";
                                      const sideEffectsValue = apiData?.side_effect ?? apiData?.side_effects ?? formData?.side_effects ?? "";
                                      const unitOfMeasureValue = apiData?.unit_of_measurement ?? apiData?.unit_of_measure ?? formData?.unit_of_measure ?? "";
                                      
                                      // Use setTimeout to ensure form is ready before setting values
                                      setTimeout(() => {
                                        setValue(`medications[${fieldIndex}].instructions`, instructionsValue, { shouldValidate: false });
                                        setValue(`medications[${fieldIndex}].side_effects`, sideEffectsValue, { shouldValidate: false });
                                        setValue(`medications[${fieldIndex}].unit_of_measure`, unitOfMeasureValue, { shouldValidate: false });
                                      }, 0);
                                      
                                      setEditableIndex(fieldIndex);
                                      // Collapse all other rows and expand this one
                                      setExpandedRows(new Set([medicationId]));
                                    }
                                  }
                                }}
                                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1 ${
                                  editableIndex === fieldIndex
                                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100 hover:shadow-sm'
                                }`}
                                title={editableIndex === fieldIndex ? 'Cancel Edit' : 'Edit Medication'}
                              >
                                {editableIndex === fieldIndex ? (
                                  'Cancel'
                                ) : (
                                  <>
                                    <CiEdit className='w-3.5 h-3.5' />
                                    Edit
                                  </>
                                )}
                              </button>
                              )}
                              {canCloneMedication && (
                              <button
                                type='button'
                                onClick={() => {
                                  handleCloneMedication(fieldIndex, medication);
                                }}
                                className='px-2.5 py-1.5 bg-purple-50 text-purple-600 hover:bg-purple-100 hover:shadow-sm rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1'
                                title='Clone Medication'
                              >
                                <FaCopy className='w-3.5 h-3.5' />
                                Clone
                              </button>
                              )}
                              {/* Only show delete button for saved medications (those with ID or UUID) */}
                              {canDeleteMedication && (field?.item?.id || field?.item?.uuid || medication?.id || medication?.uuid) && (
                                <button
                                  type='button'
                                  onClick={() => {
                                    handleDeleteMedication(fieldIndex, field);
                                  }}
                                  className='px-2.5 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 hover:shadow-sm rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1'
                                  title='Delete Medication'
                                >
                                  <IoTrashOutline className='w-3.5 h-3.5' />
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                      {/* Expanded Details Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={canShowMedicationActions ? 8 : 7} className='px-4 py-6 bg-gradient-to-b from-gray-50/50 to-transparent'>
                            {field?.item?.id &&
                            (field.item.updated_by_name || field.item.updater?.name) ? (
                              <div className='text-sm text-gray-600 bg-gray-50/90 border border-gray-200 rounded-lg px-4 py-2.5 mb-4 flex flex-wrap gap-x-6 gap-y-1'>
                                <span>
                                  <span className='font-semibold text-gray-700'>Medication updated by: </span>
                                  {field.item.updated_by_name || field.item.updater?.name}
                                </span>
                              </div>
                            ) : null}
                            {isEditing ? (
                              // EDIT MODE - Show editable form fields inline
                              <div className='space-y-6 bg-white rounded-lg p-6 border-2 border-blue-200 shadow-lg'>
                                <div className='flex items-center justify-between mb-4 pb-4 border-b border-gray-200'>
                                  <h3 className='text-lg font-bold text-gray-800'>Edit Medication</h3>
                                  <div className='flex items-center gap-2'>
                                    <button
                                      type='button'
                                      onClick={() => {
                                        if (editLoader) return;
                                        handleEdit(fieldIndex, field);
                                      }}
                                      disabled={editLoader}
                                      className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 font-medium disabled:opacity-50 
                                        
                                           bg-green-500 text-white hover:bg-green-700
                                      `}
                                    >
                                      {editLoader ? (
                                        <>
                                          {/* <CircularLoader size={16} /> */}
                                          Saving...
                                        </>
                                      ) : (
                                        <>
                                          <FiSave className='w-4 h-4' />
                                          Save
                                        </>
                                      )}
                                    </button>
                                    <button
                                      type='button'
                                      onClick={() => {
                                        // If it's a new medication (not saved), remove it from the form
                                        const isNewMedication = !field?.item?.id && !field?.item?.uuid && !medication?.id && !medication?.uuid;
                                        if (isNewMedication) {
                                          remove(fieldIndex);
                                          // Also collapse the row if it was expanded
                                          const medicationId = medication?.id || `temp-${fieldIndex}`;
                                          const newExpanded = new Set(expandedRows);
                                          newExpanded.delete(medicationId);
                                          setExpandedRows(newExpanded);
                                        } else {
                                          // For saved medications, just exit edit mode
                                          setEditableIndex(null);
                                        }
                                      }}
                                      className='px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium'
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>

                                <MedicationPractitionerWarning />

                                {/* Basic Information */}
                                <div className='grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4'>
                                  <div className='flex flex-col gap-2'>
                                    <label className='font-semibold text-sm text-gray-700'>
                                      Medicine Type <span className='text-red-500'>*</span>
                                    </label>
                                    {(() => {
                                      // V360-4714: Determine if this is an existing medication (has ID or UUID)
                                      const isExistingMedication = !!(
                                        medication?.id || 
                                        medication?.uuid || 
                                        field?.item?.id || 
                                        field?.item?.uuid
                                      );
                                      
                                      return isExistingMedication ? (
                                        // Wrap disabled field with tooltip for existing medications
                                        <PopoverContainer
                                          cssClass="medication-tooltip"
                                          toolTip="Medicine Type cannot be updated for existing medications"
                                        >
                                          <div>
                                            <Controller
                                              name={`medications[${fieldIndex}].medicine_type`}
                                              control={control}
                                              render={({ field }) => {
                                                return (
                                                  <DropdownWithIconInBoundary
                                                    Icon={MyIcon}
                                                    options={MEDICINE_TYPE}
                                                    {...field}
                                                    isDisabled={true}
                                                  />
                                                );
                                              }}
                                            />
                                          </div>
                                        </PopoverContainer>
                                      ) : (
                                        <Controller
                                          name={`medications[${fieldIndex}].medicine_type`}
                                          control={control}
                                          render={({ field }) => {
                                            const currentEventType = watch(`medications[${fieldIndex}].event_type`);
                                            return (
                                              <DropdownWithIconInBoundary
                                                Icon={MyIcon}
                                                options={MEDICINE_TYPE}
                                                {...field}
                                                onChange={(selectedOption) => {
                                                  const newValue = selectedOption?.value || selectedOption;
                                                  field.onChange(newValue);
                                                  // Clear error when field is updated
                                                  if (newValue) {
                                                    clearErrors(`medications[${fieldIndex}].medicine_type`);
                                                  }
                                                  
                                                  // If changing from PRN to Routine, reset event_type if it's 'prn'
                                                  if (newValue === "routine" && currentEventType === "prn") {
                                                    setValue(`medications[${fieldIndex}].event_type`, "");
                                                    // Also clear related fields
                                                    setValue(`medications[${fieldIndex}].event_name`, "");
                                                    setValue(`medications[${fieldIndex}].time`, "");
                                                  }
                                                  // If changing from Routine to PRN, allow event_type to be 'prn'
                                                  // but don't force it - let user choose
                                                }}
                                              />
                                            );
                                          }}
                                        />
                                      );
                                    })()}
                                    {errors?.medications?.[fieldIndex]?.medicine_type && (
                                      <span className='text-xs text-red-500'>
                                        {errors.medications[fieldIndex].medicine_type.message}
                                      </span>
                                    )}
                                  </div>

                                  <div className='flex flex-col gap-2'>
                                    <label className='font-semibold text-sm text-gray-700'>
                                      Medicine Name <span className='text-red-500'>*</span>
                                    </label>
                                    {(() => {
                                      // V360-4714: Determine if this is an existing medication (has ID or UUID)
                                      const isExistingMedication = !!(
                                        medication?.id || 
                                        medication?.uuid || 
                                        field?.item?.id || 
                                        field?.item?.uuid
                                      );
                                      
                                      return isExistingMedication ? (
                                        // Wrap disabled field with tooltip for existing medications
                                        <PopoverContainer
                                          cssClass="medication-tooltip"
                                          toolTip="Medicine Name cannot be updated for existing medications"
                                        >
                                          <div>
                                            <InputField
                                              backgroundColor='#fff'
                                              register={register}
                                              setValue={setValue}
                                              control={control}
                                              name={`medications[${fieldIndex}].medicine_name`}
                                              readOnly={true}
                                              isMedication={true}
                                            />
                                          </div>
                                        </PopoverContainer>
                                      ) : (
                                        <InputField
                                          backgroundColor='#fff'
                                          register={register}
                                          setValue={setValue}
                                          control={control}
                                          name={`medications[${fieldIndex}].medicine_name`}
                                          readOnly={false}
                                          isMedication={true}
                                          onChange={(e) => {
                                            setValue(`medications[${fieldIndex}].medicine_name`, e.target.value);
                                            if (e.target.value) {
                                              clearErrors(`medications[${fieldIndex}].medicine_name`);
                                            }
                                          }}
                                        />
                                      );
                                    })()}
                                    {errors?.medications?.[fieldIndex]?.medicine_name && (
                                      <span className='text-xs text-red-500'>
                                        {errors.medications[fieldIndex].medicine_name.message}
                                      </span>
                                    )}
                                  </div>

                                  <div className='flex flex-col gap-2'>
                                    <label className='font-semibold text-sm text-gray-700'>
                                      Start Date <span className='text-red-500'>*</span>
                                    </label>
                                    {(() => {
                                      // V360-4714: Determine if this is an existing medication (has ID or UUID)
                                      const isExistingMedication = !!(
                                        medication?.id || 
                                        medication?.uuid || 
                                        field?.item?.id || 
                                        field?.item?.uuid
                                      );
                                      
                                      // Get the original start date for existing medications (to restore if cleared)
                                      const originalStartDate = isExistingMedication 
                                        ? (medication?.duration_start || field?.item?.duration_start || medData?.duration_start)
                                        : null;
                                      
                                      return isExistingMedication ? (
                                        // Wrap disabled field with tooltip for existing medications
                                        <PopoverContainer
                                          cssClass="medication-tooltip"
                                          toolTip="Start Date cannot be updated for existing medications"
                                        >
                                          <div>
                                            <DateRangePickerNew
                                              isSingleDate
                                              isDisabled={true}
                                              isClearable={false}
                                              handleCancel={() => {
                                                // For existing medications, restore the original date if somehow cleared
                                                if (originalStartDate) {
                                                  setValue(`medications[${fieldIndex}].duration_start`, originalStartDate);
                                                  toast.error("Start Date cannot be removed for existing medications.");
                                                }
                                              }}
                                              handleChange={(obj) => {
                                                // For existing medications, restore the original date and show error
                                                if (obj) {
                                                  // Allow setting date (though disabled, this is a safeguard)
                                                  setValue(`medications[${fieldIndex}].duration_start`, obj);
                                                } else if (originalStartDate) {
                                                  setValue(`medications[${fieldIndex}].duration_start`, originalStartDate);
                                                  toast.error("Start Date cannot be removed for existing medications.");
                                                }
                                              }}
                                              singleSelectedDate={medData?.duration_start ? new Date(medData.duration_start) : null}
                                              minDate={new Date()}
                                              showCalendarIcon
                                            />
                                          </div>
                                        </PopoverContainer>
                                      ) : (
                                        <DateRangePickerNew
                                          isSingleDate
                                          isClearable
                                          handleCancel={() => {
                                            setValue(`medications[${fieldIndex}].duration_start`, null);
                                            clearErrors(`medications[${fieldIndex}].duration_start`);
                                          }}
                                          handleChange={(obj) => {
                                            if (obj) {
                                              setValue(`medications[${fieldIndex}].duration_start`, obj);
                                              clearErrors(`medications[${fieldIndex}].duration_start`);
                                            }
                                          }}
                                          singleSelectedDate={medData?.duration_start ? new Date(medData.duration_start) : null}
                                          minDate={new Date()}
                                          showCalendarIcon
                                        />
                                      );
                                    })()}
                                    {errors?.medications?.[fieldIndex]?.duration_start && (
                                      <span className='text-xs text-red-500'>
                                        {errors.medications[fieldIndex].duration_start.message}
                                      </span>
                                    )}
                                  </div>

                                  <div className='flex flex-col gap-2'>
                                    <label className='font-semibold text-sm text-gray-700'>End Date</label>
                                    <DateRangePickerNew
                                      isSingleDate
                                      isClearable
                                      handleCancel={() => setValue(`medications[${fieldIndex}].duration_end`, null)}
                                      handleChange={(obj) => obj && setValue(`medications[${fieldIndex}].duration_end`, obj)}
                                      singleSelectedDate={medData?.duration_end ? new Date(medData.duration_end) : null}
                                      maxDate={medData?.duration_start ? addMonths(medData.duration_start, 3) : null}
                                      minDate={medData?.duration_start ? (() => {
                                        const startDate = new Date(medData.duration_start);
                                        startDate.setDate(startDate.getDate() + 1);
                                        return startDate;
                                      })() : new Date()}
                                      showCalendarIcon
                                    />
                                  </div>
                                </div>

                                {/* Event & Timing */}
                                <div className='grid sm:grid-cols-1 md:grid-cols-3 gap-4'>
                                  <div className='flex flex-col gap-2'>
                                    <label className='font-semibold text-sm text-gray-700'>
                                      Event <span className='text-red-500'>*</span>
                                    </label>
                                    <Controller
                                      name={`medications[${fieldIndex}].event_type`}
                                      control={control}
                                      rules={{
                                        required: "Event is required",
                                        validate: (value) => {
                                          // Get current medicine_type value to ensure we have the latest
                                          const currentMedicineType = getValues(`medications[${fieldIndex}].medicine_type`);
                                          // For Routine medications, event_type cannot be 'prn'
                                          if (currentMedicineType === "routine" && value === "prn") {
                                            return "Routine medications cannot have PRN as event type";
                                          }
                                          return true;
                                        }
                                      }}
                                      render={({ field }) => {
                                        const eventOptions = medData?.medicine_type === "prn"
                                          ? [...MEDICINE_EVENT, { label: "PRN", value: "prn" }]
                                          : MEDICINE_EVENT;
                                        return (
                                          <DropdownWithIconInBoundary
                                            Icon={TimeIcon}
                                            options={eventOptions}
                                            {...field}
                                            onChange={(selectedOption) => {
                                              const newValue = selectedOption?.value || selectedOption;
                                              field.onChange(newValue);
                                              // Clear error when field is updated
                                              if (newValue) {
                                                clearErrors(`medications[${fieldIndex}].event_type`);
                                              }
                                              
                                              // Clear related fields when event_type changes
                                              if (newValue !== "event") {
                                                setValue(`medications[${fieldIndex}].event_name`, "");
                                                clearErrors(`medications[${fieldIndex}].event_name`);
                                              }
                                              if (newValue !== "time") {
                                                setValue(`medications[${fieldIndex}].time`, "");
                                                clearErrors(`medications[${fieldIndex}].time`);
                                              }
                                            }}
                                          />
                                        );
                                      }}
                                    />
                                    {errors?.medications?.[fieldIndex]?.event_type && (
                                      <span className='text-xs text-red-500'>
                                        {errors.medications[fieldIndex].event_type.message}
                                      </span>
                                    )}
                                  </div>

                                  {medData?.event_type === "event" && (
                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700'>
                                        Choose Event <span className='text-red-500'>*</span>
                                      </label>
                                      <Controller
                                        name={`medications[${fieldIndex}].event_name`}
                                        control={control}
                                        rules={{
                                          required: "Choose Event is required",
                                          validate: (value) => {
                                            const currentEventType = getValues(`medications[${fieldIndex}].event_type`);
                                            if (currentEventType === "event" && !value) {
                                              return "Choose Event is required";
                                            }
                                            return true;
                                          }
                                        }}
                                        render={({ field }) => (
                                          <DropdownWithIconInBoundary
                                            Icon={TimeIcon}
                                            options={CHOOSE_EVENT}
                                            {...field}
                                            onChange={(selectedOption) => {
                                              const newValue = selectedOption?.value || selectedOption;
                                              field.onChange(newValue);
                                              // Clear error when field is updated
                                              if (newValue) {
                                                clearErrors(`medications[${fieldIndex}].event_name`);
                                              }
                                            }}
                                          />
                                        )}
                                      />
                                      {errors?.medications?.[fieldIndex]?.event_name && (
                                        <span className='text-xs text-red-500'>
                                          {errors.medications[fieldIndex].event_name.message}
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {medData?.event_type === "time" && (
                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700'>
                                        Time <span className='text-red-500'>*</span>
                                      </label>
                                      <InputField
                                        name={`medications[${fieldIndex}].time`}
                                        type='time'
                                        backgroundColor='#fff'
                                        register={register}
                                        setValue={setValue}
                                        control={control}
                                        isMedication={true}
                                        onChange={(e) => {
                                          setValue(`medications[${fieldIndex}].time`, e.target.value);
                                          if (e.target.value) {
                                            clearErrors(`medications[${fieldIndex}].time`);
                                          }
                                        }}
                                      />
                                      {errors?.medications?.[fieldIndex]?.time && (
                                        <span className='text-xs text-red-500'>
                                          {errors.medications[fieldIndex].time.message}
                                        </span>
                                      )}
                                    </div>
                                  )}

                                  {/* <div className='flex flex-col gap-2'>
                                    <label className='font-semibold text-sm text-gray-700'>
                                      Select Quantity {medData?.event_type !== "prn" && <span className='text-red-500'>*</span>}
                                    </label>
                                    <input
                                      {...register(`medications[${fieldIndex}].select_quantity`, {
                                        required: medData?.event_type !== "prn" ? "This field is required" : false,
                                        pattern: {
                                          value: /^(0(\.\d{1,2})?|[1-9](\.\d{1,2})?)$/,
                                          message: "Please enter a value between 0.1 and 9",
                                        },
                                      })}
                                      name={`medications[${fieldIndex}].select_quantity`}
                                      type='number'
                                      className='did-floating-input'
                                      step='0.01'
                                      min='0.1'
                                      max='9'
                                    />
                                  </div> */}
                                </div>

                                {/* Dosage Information */}
                                <div className='border-t border-gray-200 pt-4'>
                                  <h4 className='font-semibold text-sm text-gray-700 mb-3 flex items-center gap-2'>
                                    <span className='w-1 h-4 bg-blue-500 rounded'></span>
                                    Dosage Information
                                  </h4>
                                  <div className='grid sm:grid-cols-1 md:grid-cols-2 gap-4'>
                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700'>
                                        Quantity <span className='text-red-500'>*</span>
                                      </label>
                                      <input
                                        {...register(`medications[${fieldIndex}].select_quantity`, {
                                          required: "Quantity is required",
                                          validate: (value) => {
                                            if (!value || value === '') {
                                              return "Quantity is required";
                                            }
                                            const numValue = parseFloat(value);
                                            if (isNaN(numValue)) {
                                              return "Quantity must be a valid number";
                                            }
                                            if (numValue < 0) {
                                              return "Quantity must be 0 or greater";
                                            }
                                            // Check if value has more than 2 decimal places
                                            if (value.includes('.') && value.split('.')[1]?.length > 2) {
                                              return "Quantity can have maximum 2 decimal places";
                                            }
                                            return true;
                                          },
                                        })}
                                        name={`medications[${fieldIndex}].select_quantity`}
                                        type='number'
                                        className='did-floating-input'
                                        step='0.01'
                                        min='0'
                                        placeholder='Enter quantity'
                                        onKeyDown={(e) => {
                                          // Prevent typing negative sign or values outside range
                                          if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                                            e.preventDefault();
                                          }
                                        }}
                                        onChange={(e) => {
                                          let value = e.target.value;
                                          
                                          // Allow empty value for clearing
                                          if (value === '') {
                                            setValue(`medications[${fieldIndex}].select_quantity`, '');
                                            clearErrors(`medications[${fieldIndex}].select_quantity`);
                                            return;
                                          }
                                          
                                          // Remove any non-numeric characters except decimal point
                                          value = value.replace(/[^0-9.]/g, '');
                                          
                                          // Prevent multiple decimal points
                                          const parts = value.split('.');
                                          if (parts.length > 2) {
                                            value = parts[0] + '.' + parts.slice(1).join('');
                                          }
                                          
                                          // Limit to 2 decimal places
                                          if (parts.length === 2 && parts[1].length > 2) {
                                            value = parts[0] + '.' + parts[1].substring(0, 2);
                                          }
                                          
                                          const numValue = parseFloat(value);
                                          
                                          // If value is valid number, check range
                                          if (!isNaN(numValue)) {
                                            // Prevent negative values - set to 0
                                            if (numValue < 0) {
                                              setValue(`medications[${fieldIndex}].select_quantity`, '0');
                                              setError(`medications[${fieldIndex}].select_quantity`, {
                                                type: "validate",
                                                message: "Quantity must be 0 or greater",
                                              });
                                              return;
                                            }
                                            
                                            // Valid value - set it and clear errors
                                            setValue(`medications[${fieldIndex}].select_quantity`, value);
                                            clearErrors(`medications[${fieldIndex}].select_quantity`);
                                          } else {
                                            // Invalid input - keep the cleaned value but don't clear errors
                                            setValue(`medications[${fieldIndex}].select_quantity`, value);
                                          }
                                        }}
                                        onBlur={(e) => {
                                          const value = e.target.value;
                                          if (value && parseFloat(value) >= 0) {
                                            clearErrors(`medications[${fieldIndex}].select_quantity`);
                                          }
                                        }}
                                      />
                                      {errors?.medications?.[fieldIndex]?.select_quantity && (
                                        <span className='text-xs text-red-500'>{errors.medications[fieldIndex].select_quantity.message}</span>
                                      )}
                                    </div>

                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700'>
                                        Unit of Measure <span className='text-red-500'>*</span>
                                      </label>
                                      <Controller
                                        name={`medications[${fieldIndex}].unit_of_measure`}
                                        control={control}
                                        rules={{ required: "Unit of measure is required" }}
                                        render={({ field: controllerField }) => {
                                          // Get value from API data (field.item) if form value is empty
                                          // Note: API returns unit_of_measurement, but we use unit_of_measure in form
                                          const apiData = field?.item;
                                          const apiValue = apiData?.unit_of_measurement ?? apiData?.unit_of_measure;
                                          const formValue = controllerField.value || medData?.unit_of_measure;
                                          // Use API value if form value is empty, otherwise use form value
                                          const displayValue = formValue || apiValue || "";
                                          
                                          return (
                                            <DropdownWithIconInBoundary
                                              Icon={MyIcon}
                                              options={UNIT_OF_MEASURE}
                                              {...controllerField}
                                              value={displayValue}
                                              onChange={(selectedOption) => {
                                                const newValue = selectedOption?.value || selectedOption;
                                                controllerField.onChange(newValue);
                                                // Clear error when field is updated
                                                if (newValue) {
                                                  clearErrors(`medications[${fieldIndex}].unit_of_measure`);
                                                }
                                              }}
                                            />
                                          );
                                        }}
                                      />
                                      {errors?.medications?.[fieldIndex]?.unit_of_measure && (
                                        <span className='text-xs text-red-500'>{errors.medications[fieldIndex].unit_of_measure.message}</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Instructions & Side Effects */}
                                <div className='border-t border-gray-200 pt-4'>
                                  <div className='grid sm:grid-cols-1 md:grid-cols-2 gap-4'>
                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700 flex items-center gap-2'>
                                        <span className='w-1 h-4 bg-green-500 rounded'></span>
                                        Medication Instructions
                                      </label>
                                      <textarea
                                        {...register(`medications[${fieldIndex}].instructions`)}
                                        name={`medications[${fieldIndex}].instructions`}
                                        className='did-floating-input min-h-[100px] resize-y p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-green-500'
                                        rows='4'
                                        placeholder='Write details of how and when the medication should be administered...'
                                        key={`instructions-${fieldIndex}-${editableIndex === fieldIndex ? 'edit' : 'view'}-${field?.item?.id || field?.item?.uuid || 'new'}`}
                                        defaultValue={(() => {
                                          // Priority: field.item (API data) > medData (form data) > medication (fallback)
                                          // Check multiple possible field names for instructions
                                          const apiData = field?.item;
                                          return apiData?.medication_instruction ?? apiData?.medication_instructions ?? apiData?.instruction ?? apiData?.instructions ?? medData?.instructions ?? medication?.instructions ?? "";
                                        })()}
                                      />
                                    </div>

                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700 flex items-center gap-2'>
                                        <span className='w-1 h-4 bg-orange-500 rounded'></span>
                                        Side Effects
                                      </label>
                                      <textarea
                                        {...register(`medications[${fieldIndex}].side_effects`)}
                                        name={`medications[${fieldIndex}].side_effects`}
                                        className='did-floating-input min-h-[100px] resize-y p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500'
                                        rows='4'
                                        placeholder='Write any known or observed side effects of this medicine.'
                                        defaultValue={(() => {
                                          // Priority: field.item (API data) > medData (form data) > medication (fallback)
                                          // Note: API returns side_effect (singular), but we use side_effects (plural) in form
                                          const apiData = field?.item;
                                          return apiData?.side_effect ?? apiData?.side_effects ?? medData?.side_effects ?? medication?.side_effects ?? "";
                                        })()}
                                      />
                                    </div>
                                  </div>
                                </div>

                                {/* ADRS Section */}
                                <div className='border-t-2 border-red-200 bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-5'>
                                  <div className='flex items-center gap-2 mb-4'>
                                    <FaExclamationTriangle className='w-5 h-5 text-red-600' />
                                    <h4 className='text-lg font-bold text-gray-800'>Allergies & Adverse Drug Reactions (ADRS)</h4>
                                    <span className='text-xs text-gray-500 italic'>(for this medication)</span>
                                  </div>
                                  <div className='grid md:grid-cols-2 gap-4'>
                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700 flex items-center gap-2'>
                                        <FaPills className='w-4 h-4 text-red-500' />
                                        Medicines Type/Name
                                      </label>
                                      <InputField
                                        backgroundColor='#fff'
                                        register={register}
                                        setValue={setValue}
                                        control={control}
                                        name={`medications[${fieldIndex}].adrs_medicine_name`}
                                        placeholder='e.g., Penicillin, Aspirin...'
                                        isMedication={true}
                                      />
                                    </div>
                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700 flex items-center gap-2'>
                                        <HiExclamationCircle className='w-4 h-4 text-red-500' />
                                        Allergy Name
                                      </label>
                                      <InputField
                                        backgroundColor='#fff'
                                        register={register}
                                        setValue={setValue}
                                        control={control}
                                        name={`medications[${fieldIndex}].adrs_allergic_name`}
                                        placeholder='e.g., Drug Allergy, Food Allergy...'
                                        isMedication={true}
                                      />
                                    </div>
                                    <div className='flex flex-col gap-2'>
                                      <label className='font-semibold text-sm text-gray-700 flex items-center gap-2'>
                                        <FaExclamationTriangle className='w-4 h-4 text-orange-500' />
                                        Reaction Type
                                      </label>
                                      <InputField
                                        backgroundColor='#fff'
                                        register={register}
                                        setValue={setValue}
                                        control={control}
                                        name={`medications[${fieldIndex}].adrs_reaction_type`}
                                        placeholder='e.g., Rash, Nausea, Difficulty Breathing...'
                                        isMedication={true}
                                      />
                                    </div>
                                    <div className='flex flex-col gap-2 md:col-span-2'>
                                      <label className='font-semibold text-sm text-gray-700 flex items-center gap-2'>
                                        <FaNotesMedical className='w-4 h-4 text-blue-500' />
                                        Allergy Notes
                                      </label>
                                      <textarea
                                        {...register(`medications[${fieldIndex}].adrs_allergic_note`)}
                                        name={`medications[${fieldIndex}].adrs_allergic_note`}
                                        className='did-floating-input min-h-[100px] resize-y p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500'
                                        rows='4'
                                        placeholder='Write down any additional details, severity, date of occurrence...'
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // VIEW MODE - Show read-only information
                              <div className='grid md:grid-cols-2 gap-4'>
                                {/* Left Column */}
                                <div className='space-y-4'>
                                  <div className='bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl p-4 border border-blue-200/50 shadow-sm'>
                                    <div className='flex items-center gap-2 mb-2'>
                                      <div className='w-1 h-5 bg-blue-500 rounded-full'></div>
                                      <div className='text-xs font-bold text-blue-800 uppercase tracking-wide'>Dosage Information</div>
                                    </div>
                                    <div className='text-lg font-bold text-blue-900 mb-1'>
                                      {dosageQuantity !== undefined &&
                                      dosageQuantity !== null &&
                                      dosageQuantity !== "" ? (
                                        formatDosageDisplay(dosageQuantity, dosageUom)
                                      ) : (
                                        <span className='text-gray-400'>N/A</span>
                                      )}
                                    </div>
                                    {(medication?.unit_of_measure || medData?.unit_of_measure) && (
                                      <div className='text-xs font-medium text-blue-700 mt-2 pt-2 border-t border-blue-200/50'>
                                        Unit of Measure:{" "}
                                        <span className='font-semibold'>
                                          {formatUOM(dosageUom, dosageQuantity)}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  <div className='bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-4 border border-green-200/50 shadow-sm'>
                                    <div className='flex items-center gap-2 mb-2'>
                                      <div className='w-1 h-5 bg-green-500 rounded-full'></div>
                                      <div className='text-xs font-bold text-green-800 uppercase tracking-wide'>Schedule Details</div>
                                    </div>
                                    <div className='text-lg font-bold text-green-900 mb-1'>{schedule}</div>
                                    <div className='text-xs font-medium text-green-700 mt-2 pt-2 border-t border-green-200/50'>
                                      Type: <span className='font-semibold'>{medication?.event_type === "time" ? "Time-based" : medication?.event_type === "event" ? "Event-based" : "As Needed (PRN)"}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Right Column */}
                                <div className='space-y-4'>
                                  {(medication?.instructions || medData?.instructions) && (
                                    <div className='bg-white rounded-xl p-4 border border-gray-200 shadow-sm'>
                                      <div className='flex items-center gap-2 mb-3'>
                                        <div className='w-1 h-5 bg-green-500 rounded-full'></div>
                                        <span className='text-xs font-bold text-gray-800 uppercase tracking-wide'>Medication Instructions</span>
                                      </div>
                                      <p className='text-sm text-gray-700 leading-relaxed whitespace-pre-wrap'>
                                        {medication?.instructions || medData?.instructions}
                                      </p>
                                    </div>
                                  )}

                                  {(medication?.side_effects || medData?.side_effects) && (
                                    <div className='bg-white rounded-xl p-4 border border-gray-200 shadow-sm'>
                                      <div className='flex items-center gap-2 mb-3'>
                                        <div className='w-1 h-5 bg-orange-500 rounded-full'></div>
                                        <span className='text-xs font-bold text-gray-800 uppercase tracking-wide'>Side Effects</span>
                                      </div>
                                      <p className='text-sm text-gray-700 leading-relaxed whitespace-pre-wrap'>
                                        {medication?.side_effects || medData?.side_effects}
                                      </p>
                                    </div>
                                  )}

                                  {/* ADRS Section - Always visible if data exists */}
                                  {(() => {
                                    // Check both API field names (allergic_*) and mapped field names (adrs_*)
                                    const hasAdrsData = !!(medData?.adrs_medicine_name || medication?.allergic_medicine_name ||
                                                           medData?.adrs_allergic_name || medication?.allergic_name ||
                                                           medData?.adrs_reaction_type || medication?.reaction_type ||
                                                           medData?.adrs_allergic_note || medication?.allergic_note);
                                    
                                    if (!hasAdrsData) return null;

                                    const adrsMedicineName = medData?.adrs_medicine_name || medication?.allergic_medicine_name || "";
                                    const adrsAllergicName = medData?.adrs_allergic_name || medication?.allergic_name || "";
                                    const adrsReactionType = medData?.adrs_reaction_type || medication?.reaction_type || "";
                                    const adrsAllergicNote = medData?.adrs_allergic_note || medication?.allergic_note || "";

                                    return (
                                      <div className='bg-gradient-to-br from-red-50 via-pink-50/50 to-red-50/30 rounded-xl p-5 border-2 border-red-200/50 shadow-md'>
                                        <div className='flex items-center gap-2.5 mb-4 pb-3 border-b border-red-200/50'>
                                          <div className='w-10 h-10 rounded-full bg-red-100 flex items-center justify-center'>
                                            <FaExclamationTriangle className='w-5 h-5 text-red-600' />
                                          </div>
                                          <div>
                                            <span className='text-sm font-bold text-gray-900 block'>Allergies & Adverse Drug Reactions</span>
                                            <span className='text-xs text-gray-600 font-medium'>(ADRS)</span>
                                          </div>
                                        </div>
                                        <div className='grid md:grid-cols-2 gap-4'>
                                          {adrsMedicineName && (
                                            <div className='bg-white rounded-lg p-3 border border-red-100 shadow-sm'>
                                              <div className='flex items-center gap-2 mb-2'>
                                                <FaPills className='w-3.5 h-3.5 text-red-500' />
                                                <span className='text-xs font-bold text-gray-700 uppercase tracking-wide'>Medicines Type/Name</span>
                                              </div>
                                              <p className='text-sm font-medium text-gray-900'>
                                                {adrsMedicineName}
                                              </p>
                                            </div>
                                          )}
                                          {adrsAllergicName && (
                                            <div className='bg-white rounded-lg p-3 border border-red-100 shadow-sm'>
                                              <div className='flex items-center gap-2 mb-2'>
                                                <HiExclamationCircle className='w-3.5 h-3.5 text-red-500' />
                                                <span className='text-xs font-bold text-gray-700 uppercase tracking-wide'>Allergy Name</span>
                                              </div>
                                              <p className='text-sm font-medium text-gray-900'>
                                                {adrsAllergicName}
                                              </p>
                                            </div>
                                          )}
                                          {adrsReactionType && (
                                            <div className='bg-white rounded-lg p-3 border border-red-100 shadow-sm'>
                                              <div className='flex items-center gap-2 mb-2'>
                                                <FaExclamationTriangle className='w-3.5 h-3.5 text-orange-500' />
                                                <span className='text-xs font-bold text-gray-700 uppercase tracking-wide'>Reaction Type</span>
                                              </div>
                                              <p className='text-sm font-medium text-gray-900'>
                                                {adrsReactionType}
                                              </p>
                                            </div>
                                          )}
                                          {adrsAllergicNote && (
                                            <div className='md:col-span-2 bg-white rounded-lg p-3 border border-red-100 shadow-sm'>
                                              <div className='flex items-center gap-2 mb-2'>
                                                <FaNotesMedical className='w-3.5 h-3.5 text-blue-500' />
                                                <span className='text-xs font-bold text-gray-700 uppercase tracking-wide'>Allergy Notes</span>
                                              </div>
                                              <p className='text-sm text-gray-700 leading-relaxed whitespace-pre-wrap'>
                                                {adrsAllergicNote}
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })()}

                                  {(!medication?.instructions && !medData?.instructions && 
                                    !medication?.side_effects && !medData?.side_effects &&
                                    !medData?.adrs_medicine_name && !medication?.allergic_medicine_name &&
                                    !medData?.adrs_allergic_name && !medication?.allergic_name &&
                                    !medData?.adrs_reaction_type && !medication?.reaction_type &&
                                    !medData?.adrs_allergic_note && !medication?.allergic_note) && (
                                    <div className='bg-gray-50 rounded-xl p-6 border border-gray-200 text-center'>
                                      <div className='text-sm text-gray-500 font-medium'>No additional details available</div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  };

  return (
    <main className='px-4 sm:px-6 py-4 sm:py-6'>
      <div className='space-y-6'>
        {/* Medication Table Section - Always Visible */}
        <div className='space-y-4'>
          {/* Toolbar with Search and Filters */}
          <div className='bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden'>
            <div className='p-4'>
              <div className='flex flex-col lg:flex-row lg:items-center gap-4'>
                {/* Search Bar */}
                <div className='flex-1 min-w-0'>
                  <input
                    type='text'
                    placeholder='Search medications by name...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className='w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm'
                  />
                </div>

                {/* Filter and Count */}
                <div className='flex items-center gap-3 flex-shrink-0'>
                  <div className='min-w-[150px]'>
                    <SelectOption
                      data={filterTypeOptions}
                      selectedOption={selectedFilterOption}
                      handleChange={handleFilterTypeChange}
                      placeHolder="All Types"
                      isSearch={false}
                      cssClass="text-sm"
                      useMenuPortal={true}
                      menuMinWidth="150px"
                    />
                  </div>
                  {/* V360-4706: Show loading state during initial load to prevent count flicker */}
                  <div className='text-sm text-gray-600 whitespace-nowrap min-w-[120px]'>
                    {medicationLoader ? (
                      <span className='text-gray-400 italic'>Loading medications...</span>
                    ) : (
                      <span>{filteredMedications.length} of {apiMedications.length} medications</span>
                    )}
                  </div>
                  {!roleAccess([USER_ROLE.Sp_Standard_User]) && (
                    <button
                      type='button'
                      onClick={() => {
                        // Check if there's already a newly added medication that is not safe
                        const medications = watchAllFields.medications;
                        const newlyAddedMedications = medications.filter(
                          (med) => !med.isApiData
                        );

                        if (newlyAddedMedications.length > 0) {
                          // Check if any newly added medication is not safe
                          const unsafeMedication = newlyAddedMedications.find(
                            (med) => !isMedicationSafe(med)
                          );

                          if (unsafeMedication) {
                            const now = Date.now();
                            if (now - addValidationToastShownAt.current < 3000) {
                              return; // Avoid multiple toasts when user clicks add repeatedly
                            }
                            addValidationToastShownAt.current = now;
                            toast.error(
                              "Please complete and save the current medication before adding a new one."
                            );
                            return;
                          }
                        }

                        const newMedication = {
                          medicine_type: "",
                          medicine_name: "",
                          event_type: "",
                          time: "",
                          select_quantity: "",
                          event_name: "",
                          duration_start: null,
                          duration_end: null,
                          unit_of_measure: "",
                          // quantity: "",
                          instructions: "",
                          side_effects: "",
                          adrs_medicine_name: "",
                          adrs_allergic_name: "",
                          adrs_reaction_type: "",
                          adrs_allergic_note: "",
                          isApiData: false, // Mark as newly added medication
                        };
                        // Calculate the new index before append (it will be the last index after append)
                        const newIndex = fields.length;
                        const newMedicationId = `temp-${newIndex}`;
                        
                        append(newMedication);
                        
                        // Collapse all other rows and expand the new one
                        setExpandedRows(new Set([newMedicationId]));
                        setEditableIndex(newIndex);
                        
                        // Auto-scroll to the new row after DOM updates
                        setTimeout(() => {
                          const rowElement = document.querySelector(`[data-medication-id="${newMedicationId}"]`);
                          if (rowElement) {
                            rowElement.scrollIntoView({ 
                              behavior: 'smooth', 
                              block: 'center',
                              inline: 'nearest'
                            });
                          }
                        }, 200);
                      }}
                      className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium shadow-sm'
                    >
                      <img src={AddIcon} alt='Add' className='w-4 h-4' />
                      Add Medication
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Medication Table */}
          {renderListView()}
        </div>
      </div>
      {openDeleteModal && (
        <DeleteModal
          openDeleteModal={openDeleteModal}
          closeModal={closeModal}
          handleDelete={handleDeleteMedication}
          index={selectedIndex}
          selectDeletedMedication={selectDeletedMedication}
          remove={remove}
          setDeleteLoader={setDeleteLoader}
          deleteLoader={deleteLoader}
        />
      )}
    </main>
  );
};

export default MedicationApplicationRecord;
